import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Crosshair, Rss, KeySquare, Radar, Newspaper, BarChart3, Play, Loader2, Clock, Power } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';
import SignalFeed from '../components/hunter/SignalFeed';
import KeywordLibrary from '../components/hunter/KeywordLibrary';
import HunterTargets from '../components/hunter/HunterTargets';
import CiteBaitPosts from '../components/hunter/CiteBaitPosts';
import Digest from '../components/hunter/Digest';

const TABS = [
  { key: 'feed', label: 'Signal Feed', Icon: Rss },
  { key: 'keywords', label: 'Keyword Library', Icon: KeySquare },
  { key: 'targets', label: 'Targets', Icon: Radar },
  { key: 'citebait', label: 'Cite-Bait Posts', Icon: Newspaper },
  { key: 'digest', label: 'Digest', Icon: BarChart3 },
];

// "1h 23m" when far out, "23m 05s" inside the last hour.
function formatCountdown(ms) {
  if (ms <= 0) return 'now';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/**
 * Hunter — GlobalAdmin signal-prospecting console.
 * Three subsections: Signal Feed, Keyword Library, Targets.
 * Light theme matching the other global_admin pages. UI + data layer only —
 * the Reddit scraper service runs separately (src/services/hunterRedditScraper.js).
 */
export default function Hunter() {
  const [activeTab, setActiveTab] = useState('feed');
  const [unreviewed, setUnreviewed] = useState(0);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null); // { type: 'ok' | 'err', text }

  // Master ON/OFF switch (hunter_settings.enabled). When off, no scanning runs at
  // all (both scrapers bail). null = still loading.
  const [hunterOn, setHunterOn] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);

  // Next-scan countdown. nextRunAt is a server epoch (ms); skewMs corrects for any
  // client/server clock difference so the countdown stays honest. scanning reflects
  // whether a YouTube scan was running as of the last schedule fetch.
  const [nextRunAt, setNextRunAt] = useState(null);
  const [skewMs, setSkewMs] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [tick, setTick] = useState(Date.now()); // forces a re-render each second
  const refetchedAtZero = useRef(false);
  const wasScanning = useRef(false);

  const refreshUnreviewed = useCallback(async () => {
    const { count } = await supabase
      .from('hunter_signals')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'unreviewed');
    setUnreviewed(count || 0);
  }, []);

  const refreshSchedule = useCallback(async () => {
    try {
      const res = await fetchWithCsrf('/api/admin/hunter/schedule');
      if (!res.ok) return;
      const body = await res.json();
      if (body?.youtube?.nextRunAt) {
        setNextRunAt(body.youtube.nextRunAt);
        setScanning(!!body.youtube.isScanning);
        if (typeof body.serverNow === 'number') setSkewMs(body.serverNow - Date.now());
        refetchedAtZero.current = false;
      }
    } catch (_) {
      // Non-fatal — the countdown just won't show.
    }
  }, []);

  const refreshHunterOn = useCallback(async () => {
    const { data } = await supabase
      .from('hunter_settings')
      .select('value')
      .eq('key', 'enabled')
      .maybeSingle();
    // Default ON if the row isn't there yet (migration not applied).
    setHunterOn(data ? data.value !== false : true);
  }, []);

  const toggleHunter = useCallback(async () => {
    if (hunterOn === null) return;
    setToggleBusy(true);
    const next = !hunterOn;
    try {
      const { error } = await supabase
        .from('hunter_settings')
        .upsert({ key: 'enabled', value: next, updated_at: new Date().toISOString() }, { onConflict: 'key' });
      if (error) {
        setRunMsg({ type: 'err', text: 'Could not change Hunter state (is the hunter_settings table created?).' });
        setTimeout(() => setRunMsg(null), 6000);
      } else {
        setHunterOn(next);
      }
    } finally {
      setToggleBusy(false);
    }
  }, [hunterOn]);

  useEffect(() => {
    refreshUnreviewed();
    refreshSchedule();
    refreshHunterOn();
  }, [refreshUnreviewed, refreshSchedule, refreshHunterOn]);

  // Tick once a second to drive the countdown.
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // When the countdown reaches zero the cron is firing — re-fetch once (after a
  // short delay) to roll to the next interval and pick up the scanning state.
  const remainingMs = nextRunAt ? nextRunAt - (tick + skewMs) : null;
  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0 && !refetchedAtZero.current) {
      refetchedAtZero.current = true;
      const id = setTimeout(refreshSchedule, 8000);
      return () => clearTimeout(id);
    }
  }, [remainingMs, refreshSchedule]);

  // While a scan is running, poll the schedule until the server reports it done,
  // so "Scanning now…" reliably clears — a full run can take several minutes, well
  // past any fixed timeout. When it finishes, pull the new signal count once.
  useEffect(() => {
    if (scanning) {
      wasScanning.current = true;
      // Poll both: schedule (to detect completion) and the unreviewed count (so
      // the badge ticks up as signals land during the run).
      const id = setInterval(() => { refreshSchedule(); refreshUnreviewed(); }, 20000);
      const safetyStop = setTimeout(() => clearInterval(id), 20 * 60 * 1000); // give up after 20m
      return () => { clearInterval(id); clearTimeout(safetyStop); };
    }
    if (wasScanning.current) {
      wasScanning.current = false;
      refreshUnreviewed();
    }
  }, [scanning, refreshSchedule, refreshUnreviewed]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setRunMsg(null);
    try {
      const res = await fetchWithCsrf('/api/admin/hunter/run-now', { method: 'POST' });
      if (res.status === 202) {
        setRunMsg({ type: 'ok', text: 'Scan started — new signals appear as they are found.' });
        // Flip to scanning — the polling effect then watches until the server
        // reports the run finished, clears "Scanning…", and refreshes the count.
        setScanning(true);
      } else if (res.status === 409) {
        setRunMsg({ type: 'ok', text: 'A scan is already running.' });
      } else {
        const body = await res.json().catch(() => ({}));
        setRunMsg({ type: 'err', text: body.error || `Failed to start scan (${res.status}).` });
      }
    } catch (err) {
      setRunMsg({ type: 'err', text: 'Could not reach the server.' });
    } finally {
      setRunning(false);
      setTimeout(() => setRunMsg(null), 6000);
    }
  }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Crosshair className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Hunter</h1>
            <p className="text-sm text-gray-500">
              Listen for buyer language, turn it into cite-bait topics. Engage is rare and manual-approve.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {/* Master ON/OFF switch — when OFF, no scanning runs at all. */}
            <button
              onClick={toggleHunter}
              disabled={toggleBusy || hunterOn === null}
              title={hunterOn ? 'Hunter is ON — click to turn OFF (stops all scanning)' : 'Hunter is OFF — click to turn ON'}
              className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                hunterOn === false
                  ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              {toggleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
              {hunterOn === null ? 'Hunter…' : hunterOn ? 'Hunter: ON' : 'Hunter: OFF'}
            </button>
            <button
              onClick={runNow}
              disabled={running || hunterOn === false}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Run now
            </button>
          </div>
          {remainingMs !== null && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500" title="Automatic YouTube scan runs every 4 hours">
              <Clock className="w-3.5 h-3.5" />
              {scanning
                ? 'Scanning now…'
                : <>Next scan in <span className="font-medium text-gray-700 tabular-nums">{formatCountdown(remainingMs)}</span></>}
            </span>
          )}
          {runMsg && (
            <span className={`text-xs ${runMsg.type === 'err' ? 'text-rose-600' : 'text-gray-500'}`}>
              {runMsg.text}
            </span>
          )}
        </div>
      </div>

      {/* Master-OFF banner */}
      {hunterOn === false && (
        <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
          <Power className="w-4 h-4 flex-shrink-0" />
          <span>Hunter is <strong>OFF</strong>. No scanning runs — channel/subreddit monitoring and keyword search are both stopped. Your targets and keywords are preserved. Click <strong>Hunter: OFF</strong> to turn it back on.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(({ key, label, Icon }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {key === 'feed' && unreviewed > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium border ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-gray-100 text-gray-500 border-gray-200'
                }`}>
                  {unreviewed > 99 ? '99+' : unreviewed}
                </span>
              )}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-t" />
              )}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      {activeTab === 'feed' && <SignalFeed onCountChange={refreshUnreviewed} />}
      {activeTab === 'keywords' && <KeywordLibrary />}
      {activeTab === 'targets' && <HunterTargets />}
      {activeTab === 'citebait' && <CiteBaitPosts />}
      {activeTab === 'digest' && <Digest />}
    </div>
  );
}
